import { css, CSSProps } from '@compiled/react';
import { useIntl } from 'react-intl';

import { media } from '@atlaskit/primitives/responsive';
import UFOLabel from '@atlaskit/react-ufo/label';
import { token } from '@atlaskit/tokens';

import { wacToken } from '@/libraries/design/foundations';
import { useLocalizedHref } from '@/libraries/web-framework/src/localization/use-localized-href';
import { LinkListContainer } from '@/patterns/global-footer/src/global-footer-top-link-list-container';
import { useLocation } from '@/utils/location-provider';

import SourcetreeAppMailingListSignup from './sourcetree-app-mailing-list-signup';

// ==================================================================
// CSS styling
// ==================================================================
const containerStyle = css({
	backgroundColor: wacToken('color.neutral.light100'),
	paddingBlock: '24px',
	paddingInline: token('space.200'),
	borderRadius: '0 0 24px 24px',
	width: '100%',
	maxWidth: '1216px',
	margin: 'auto',

	[media.only.sm]: {
		paddingInline: '44px',
	},

	[media.above.md]: {
		paddingInline: '24px',
	},
});

const linksWrapperStyle = css({
	gridArea: 'links',
	display: 'grid',
	gridTemplateColumns: '1fr',
	gap: token('space.400'),

	[media.only.sm]: {
		gridTemplateColumns: 'repeat(2, minmax(0,1fr))',
		columnGap: '24px',
		rowGap: '48px',
	},

	[media.above.md]: {
		gridTemplateColumns: '1fr 1fr 2fr',
	},
});

export const ComponentStyleProps: CSSProps<unknown> = {
	margin: '0',
	padding: '0',
	border: '0',
	fontSize: '100%',
	verticalAlign: 'baseline',
};

// ==================================================================
// Rendering Functions
// ==================================================================

const headerContainerStyle = css({
	height: '32px',
	marginBlockStart: token('space.0'),
	marginBlockEnd: token('space.400'),
	display: 'flex',
	alignItems: 'center',
});

const listOfLinksColumnHeaderStyle = css({
	fontSize: '13px',
	lightHeight: '1.25',
	textTransform: 'uppercase',
	color: `${wacToken('color.primary.black')} !important`,
	fontWeight: '700 !important',
	fontFamily: 'Charlie Text',
});

const listOfLinksUlStyle = css({
	marginBlockEnd: '0',
	listStyle: 'none',
});

const textStyle = css({
	fontWeight: 400,
	fontSize: '1rem',
	margin: '0 0 24px',
	/* eslint-disable-next-line local/ensure-wac-token-usage */
	color: '#091e42',
	verticalAlign: 'baseline',
});

const connectTextStyle = css({
	fontWeight: 400,
	fontSize: '1rem',
	/* eslint-disable-next-line local/ensure-wac-token-usage */
	color: '#091e42',
	verticalAlign: 'baseline',
});

const socialButtonStandardStyle = css({
	display: 'flex',
	alignItems: 'center',
});

const socialButtonsStyle = css({
	listStyle: 'none',
	display: 'inline-block',
	float: 'left',
	height: '32px',
	width: '32px',
	margin: '0 0 0 5px',
});

const twitterLinkStyle = css({
	listStyle: 'none',
	display: 'inline-block',
	float: 'left',
	height: '32px',
	width: '32px',
	margin: '0 0 0 5px',
	color: wacToken('color.neutral.light800'),
	fontSize: '.875rem',
	lineHeight: 1.125,
	marginInlineEnd: '16px',
	textIndent: '-99999px',
	background:
		'url(https://wac-cdn.atlassian.com/assets/img/icons/social-icons/twitter.svg) no-repeat center center',
	backgroundSize: '100%',
});

const facebookLinkStyle = css({
	listStyle: 'none',
	display: 'inline-block',
	float: 'left',
	height: '32px',
	width: '32px',
	margin: '0 0 0 5px',
	color: wacToken('color.neutral.light800'),
	fontSize: '.875rem',
	lineHeight: 1.125,
	marginInlineEnd: '16px',
	textIndent: '-99999px',
	background:
		'url(https://wac-cdn.atlassian.com/assets/img/icons/social-icons/facebook-badge.svg) no-repeat center center',
	backgroundSize: '100%',
});

const socialButtonsListStyle = css({
	lineHeight: 1,
	color: wacToken('color.extended.blue1000'),
	boxSizing: 'inherit',
	margin: 0,
	padding: 0,
	border: 0,
	fontSize: '100%',
	verticalAlign: 'baseline',
	paddingInlineStart: 0,
	listStyle: 'none',
	float: 'right',
	marginInlineStart: '10px',
});

const socialButtonsGroupStyle = css({
	display: 'flex',
	alignItems: 'center',

	justifyContent: 'space-between',
	width: '100%',
});

const sourcetreeTextStyle = css({
	margin: '0',
	whiteSpace: 'nowrap',
});

const ConnectColumn = () => {
	return (
		<div>
			<div css={headerContainerStyle}>
				<h2 css={listOfLinksColumnHeaderStyle} className="eyebrow-md">
					Connect with us
				</h2>
			</div>
			<div css={listOfLinksUlStyle}>
				<p css={textStyle}>Sign up for Git articles and resources:</p>
			</div>
			<SourcetreeAppMailingListSignup />

			<div css={socialButtonsGroupStyle}>
				<div css={sourcetreeTextStyle}>
					<p css={connectTextStyle}>Connect with Sourcetree</p>
				</div>
				<div css={socialButtonStandardStyle}>
					<ul css={socialButtonsListStyle}>
						<li css={socialButtonsStyle}>
							<a
								href="https://www.facebook.com/Atlassian"
								title="Atlassian on Facebook"
								css={facebookLinkStyle}
							>
								Facebook
							</a>
						</li>
						<li css={socialButtonsStyle}>
							<a
								href="https://twitter.com/sourcetree"
								title="Follow us on Twitter"
								css={twitterLinkStyle}
							>
								Twitter
							</a>
						</li>
					</ul>
				</div>
			</div>
		</div>
	);
};

export const SourcetreeAppFooterTop = () => {
	const intl = useIntl();
	const location = useLocation();

	const resourcesLinkList = {
		headerText: intl.formatMessage({
			id: 'sourcetreeapp.top.resources',
			defaultMessage: 'Resources',
		}),
		listOfLinks: [
			{
				text: intl.formatMessage({
					id: 'sourcetreeapp.globalfooter.top.support',
					defaultMessage: 'Support',
				}),
				href: intl.formatMessage({
					id: 'sourcetreeapp.globalfooter.top.support.link',
					defaultMessage: 'https://support.atlassian.com/sourcetree',
				}),
			},
			{
				text: intl.formatMessage({
					id: 'sourcetreeapp.globalfooter.top.documentation',
					defaultMessage: 'Documentation',
				}),
				href: intl.formatMessage({
					id: 'sourcetreeapp.globalfooter.top.documentation.link',
					defaultMessage: 'https://confluence.atlassian.com/get-started-with-sourcetree',
				}),
			},
			{
				text: intl.formatMessage({
					id: 'sourcetreeapp.globalfooter.top.about.atlassian',
					defaultMessage: 'About Atlassian',
				}),
				href: intl.formatMessage({
					id: 'sourcetreeapp.globalfooter.top.about.atlassian.link',
					defaultMessage: 'https://www.atlassian.com/company',
				}),
			},
			{
				text: intl.formatMessage({
					id: 'sourcetreeapp.globalfooter.top.download.archive',
					defaultMessage: 'Download archive',
				}),
				href: intl.formatMessage({
					id: 'sourcetreeapp.globalfooter.top.download.archive.link',
					defaultMessage: 'https://sourcetreeapp.com/download-archives',
				}),
			},
		],
	};
	const sourcetreeAppLinkList = {
		listOfLinks: [
			{
				text: intl.formatMessage({
					id: 'sourcetreeapp.top.blog',
					defaultMessage: 'Blog',
				}),
				href: useLocalizedHref('/blog/sourcetree'),
			},
		],
	};

	// Adding pdf to french locale for compliance
	if (location.pathname.startsWith('/fr')) {
		sourcetreeAppLinkList.listOfLinks.push({
			text: `Index de l'égalité professionnelle`,

			href: 'https://dam-cdn.atl.orangelogic.com/AssetLink/qey7mu25028ek1rp0ru28l45l8y3fm5r/2025-France-Gender-Equality-Index.pdf',
		});
	}

	return (
		<UFOLabel name="global-footer-sourcetreeapp-top">
			<div css={containerStyle}>
				<div css={linksWrapperStyle}>
					<LinkListContainer {...sourcetreeAppLinkList} logoType="sourcetreeapp" />
					<LinkListContainer {...resourcesLinkList} />
					<ConnectColumn />
				</div>
			</div>
		</UFOLabel>
	);
};
